import {
  numberColumns,
  numberVariableColumns,
  SpreadsheetColumnsService,
  TableFilterColumns
} from "./../../../@core/utils/spreadsheet-columns.service";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NbCardModule, NbDialogService, NbIconConfig, NbToastrService} from '@nebular/theme';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {GridOptions, IRowNode, MenuItemDef, RowNode} from 'ag-grid-community';
import {combineLatest, from, Observable, of} from 'rxjs';
import {catchError, concatMap, debounceTime, filter, finalize, first, map, switchMap, tap, toArray} from 'rxjs/operators';
import {KPI} from '../../../@core/api';
import {I18nService} from '../../../@core/utils/i18n.service';
import {
  AssetsService,
  ConfigService,
  ExportsService,
  ImportsService,
  InsightFiltersService,
  InvestmentScenariiService,
  PerimetersService,
  QuickFiltersService,
  SaveToastService,
  SpreadsheetColumnsAssetsService,
  StateLevelsService,
  UsersService,
  YearsService,
} from '../../../@core/utils';
import {AssetsChangesService} from '../../../@core/utils/assets-changes.service';
import {AG_GRID_ASSET_FILTERS, ASSET_FILTER_KEY_PREFIX, TABLE_FILTER_ICON} from '../../../@core/utils/constants';
import {TechnicalStateFilterComponent} from '../ag-grid-filters/technical-state-filter.component';
import {TechnicalStateRendererComponent} from '../ag-grid-renderers/technical-state-renderer.component';
import {DatePipe} from '@angular/common';
import {AgGridBaseComponent} from '../ag-grid-base/ag-grid-base.component';
import {AgGridAngular} from 'ag-grid-angular';
import { AssetFormDrawerComponent } from "../asset-form-drawer/asset-form-drawer.component";
import Perimeter = KPI.Perimeter;
import { LastTechnicalStateFilterComponent } from "../ag-grid-filters/last-technical-state-filter.component";
import { LastTechnicalStateRendererComponent } from "../ag-grid-renderers/last-technial-state-render.component";
import { PpaDrawerComponent } from "../ppa-drawer/ppa-drawer.component";
import { SheetDialogComponent } from "../sheet-dialog/sheet-dialog.component";
import { BasicDialogComponent } from "../basic-dialog/basic-dialog.component";


@Component({
  selector: "app-ag-grid-asset",
  templateUrl: "./ag-grid-asset.component.html",
  styleUrls: ["./ag-grid-asset.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbCardModule,  AgGridAngular, PpaDrawerComponent, forwardRef(() => AssetFormDrawerComponent), TranslateModule]
})
export class AgGridAssetComponent extends AgGridBaseComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    protected baseSpreadsheetColumnsService: SpreadsheetColumnsService,
    protected quickFiltersService: QuickFiltersService,
    protected route: ActivatedRoute,
    private router: Router,
    public translate: TranslateService,
    private spreadsheetColumnsService: SpreadsheetColumnsAssetsService,
    private dialogService: NbDialogService,
    private perimetersService: PerimetersService,
    public i18n: I18nService,
    private yearsService: YearsService,
    private stateLevelsService: StateLevelsService,
    private exportsService: ExportsService,
    private investmentScenariiService: InvestmentScenariiService,
    private assetsChangesService: AssetsChangesService,
    private insightFiltersService: InsightFiltersService,
    public cdRef: ChangeDetectorRef,
    private assetsService: AssetsService,
    private config: ConfigService,
    private usersService: UsersService,
    private toastrService: NbToastrService,
    private importsService: ImportsService,
    private saveToast: SaveToastService
  ) {
    super(baseSpreadsheetColumnsService, quickFiltersService, route);
  }

  protected spreadsheetType: KPI.SpreadsheetType = "asset";
  protected override alwaysHiddenColumns = ['import_source', 'import_reference'];

  @Input() public level: number;
  @Input() public fullHeight: boolean = false;
  @Input() public tableHeight: string = '';
  @Input() public showTotals: boolean = false;
  @Input() public assetsListFilter: any;
  @Input() public filterContext: string = 'default';
  @Input() public importMode: boolean = false; // For asset import
  @Output() public assetsAndInvests = new EventEmitter<any>();
  @Output() public totalAsset = new EventEmitter<any>();
  @Output() public filtersNb = new EventEmitter<number>();
  @Output() public assetIds = new EventEmitter<number[]>();

  @Output() public selectedRowsNb = new EventEmitter<number>();
  @Output() public filteredAssetsNb = new EventEmitter<number>();
  @Output() public loadedRowsNb = new EventEmitter<number>();
  @Output() public refreshRequested = new EventEmitter<void>();

  private filtersLocalStorageKey: string;
  private cacheBlockSize = 30; // Number of rows to load at once
  public loading: boolean = false;
  public isRefreshing: boolean = false;
  public refreshLoading: boolean = false;
  public showPpaDrawer: boolean = false;
  public showEquipmentDrawer: boolean = false;
  public selectedAsset: KPI.MappedAsset = null;
  public selectedAssetIds: number[] = [];
  public gridOptions: GridOptions = {};
  public customerConfig: KPI.CustomerConfig = null;
  private isInsightAssetsPage: boolean;
  private isInsightSpreadsheetPage: boolean;
  private filterKeyPrefix = ASSET_FILTER_KEY_PREFIX;
  public title: string;
  public levelsMap: any[] = [];
  private finishedFirstFiltering = false;
  private userOpenedFilter: boolean = false;
  private noDataSentence: string = "";
  private permissions: KPI.PerimeterPermission[] = [];
  private loadingColumnsAndFilters = true; // Flag meaning it's the first grid loading
  private shouldApplyEOLFilter: boolean = false;
  public assetsWithInvests: any = {
    filteredAssetsNb: 0, // filtered assets number
    investNumber: 0,
    assetsIdsWithInvest: [], // Ids of assets with investment (used to retrieve the investment)
    loaded: false
  };

  public techStateAssets: any = {
    total: 0, // Total assets number
    techStateAssetsNb: 0, // Assets number for the technical state selected
    technicalState: null, // The technical state selected
    nextCapex: 0, // Next CAPEX
    loaded: false
  };

  public levelByNotesMap = [
    {noteFrom: null, noteTo: null, level: null},
    {noteFrom: 0, noteTo: 15, level: 0},
    {noteFrom: 16, noteTo: 35, level: 1},
    {noteFrom: 36, noteTo: 50, level: 2},
    {noteFrom: 51, noteTo: 100, level: 3},
  ];
  public endYear: number | null = null;
  public canAccessAudit: boolean;
  public datePipe = new DatePipe(this.translate.currentLang || this.i18n.getCulture());
  // private pendingUrlParams: any = null;
  private initialFilterModel: any = null;
  public style: string = '';

  ngOnInit() {
    if (this.tableHeight) {
      // I had to give it a fixed height because I don't understand how ag-grid's css works.
      // When I try to display this grid on the ai-import page, it only shows 1 or 3 rows.
      this.style = 'height: ' + this.tableHeight;
    } else {
      // this is the rule that was aldreay in place, but in the html file.
      this.style = this.fullHeight ? 'height: 100%' : 'height: 80%; margin-bottom: 10px';
    }
    this.filtersLocalStorageKey = `${AG_GRID_ASSET_FILTERS}_${this.filterContext}`;
    this.isInsightAssetsPage = this.router.url.includes('/insight/asset');
    this.isInsightSpreadsheetPage = this.router.url.includes('/insight/spreadsheet');
    this.urlParamsApplied = false;
    this.subscriptions.push(
      this.route.queryParams
        .pipe(
          filter(params => Object.keys(params).some(key => key.startsWith(this.filterKeyPrefix))),
          first()
        )
        .subscribe(params => {
          // this.pendingUrlParams = params;
          this.setInitialFilterModel(params);
        })
    );
    this.subscriptions.push(
      this.config.customerConfig$.subscribe(conf => {
        if (conf) {
          this.canAccessAudit = conf.can_access_audit_app;
          this.customerConfig = conf;
        }
      }),
      this.yearsService.endYear$.subscribe(endYear => (this.endYear = endYear)),
      this.translate
        .get("pages.common.ag-grid.no_asset_found")
        .subscribe(noDataSentence => (this.noDataSentence = noDataSentence)),
      this.usersService.currentUserPerimeterPermissions$.subscribe(permissions => (this.permissions = permissions)),
      this.baseSpreadsheetColumnsService.deleteAssetFilters$.subscribe(deleteFilters => {
        if (deleteFilters && this.gridApi) {
          Object.keys(this.gridApi.getFilterModel()).map(k => {
            this.gridApi.destroyFilter(k);
          });
          this.userOpenedFilter = true;
          this.gridApi.onFilterChanged();
          this.gridApi.closeToolPanel();
          localStorage.setItem(this.filtersLocalStorageKey, JSON.stringify({}));
          localStorage.setItem("eolFilterClearedByUser", "true");
        }
      }),
      this.baseSpreadsheetColumnsService.openAssetFilters$.subscribe(openFilters => {
        if (openFilters && this.gridApi) {
          this.gridApi.openToolPanel("columns");
        } else if (!openFilters && this.gridApi) {
          this.gridApi.closeToolPanel();
        }
      }),
      this.importsService.selectAllRows$.subscribe(selectAllRows => {
        if (selectAllRows) {
          this.gridApi.selectAll('all');
        } else if (selectAllRows === false) {
          this.clearSelection();
        }
      })
    );

    this.subscriptions.push(
      this.assetsChangesService.pendingChangesNumber$.subscribe(pendingChangesNumber => {
        if (pendingChangesNumber > 0) {
          this.loading = true;
        } else if (!this.loadingColumnsAndFilters && !pendingChangesNumber) {
          this.gridApi.refreshServerSide();
          if (!this.isRefreshing) {
            this.loading = false;
          }
        }
      }),
      this.perimetersService._perimetersHaveChanged$.pipe(
        filter(() => this.finishedFirstFiltering && !this.loadingColumnsAndFilters && !!this.gridApi)
      ).subscribe(() => {
        this.loading = true;
        this.refreshLoading = true;
        this.cdRef.detectChanges();
        this.gridApi.deselectAll();
        this.gridApi.refreshServerSide();
      }),
      combineLatest(this.stateLevelsService.levels$, this.stateLevelsService.levelsNames$).subscribe(
        ([levels, levelNames]) => {
          this.levelsMap.push({
            id: null,
            label: this.translate.instant("asset.no_notation")
          });
          levels.map(level => {
            this.levelsMap.push({
              id: level,
              label: levelNames[level]
            });
          });
          if (this.isInsightSpreadsheetPage) {
            this.techStateAssets.technicalState = levelNames[this.level];
            this.subscriptions.push(
              this.route.queryParams.subscribe(params => {
                if (Object.keys(params).length === 0) {
                  this.shouldApplyEOLFilter = true;
                }
              })
            );
          } else {
            this.subscriptions.push(
              this.route.queryParams.subscribe(params => {
                if (levelNames[params["asset_filter__level"]]) {
                  this.techStateAssets.technicalState = levelNames[params["asset_filter__level"]];
                }
              })
            );
          }
        }
      )
    );

    let rowSelection: any = 'multiple';
    if (this.importMode) {
      rowSelection = {
        mode: 'multiRow',
        enableClickSelection: false,
      }
    }

    this.gridOptions = {
      ...this.gridOptions,
      rowModelType: "serverSide",
      serverSideDatasource: this.serverSideDatasource(),
      loading: false,
      blockLoadDebounceMillis: 100,
      cacheBlockSize: this.cacheBlockSize,
      context: this,
      tooltipShowDelay: 0,
      rowBuffer: 10, // Draw 10 rows above and below the visible rows (Performance)
      allowDragFromColumnsToolPanel: true, // Allow user to move columns from panel
      cellSelection: {
        suppressMultiRanges: true
      }, // Unable rows/columns range select
      rowSelection: rowSelection,
      rowHeight: 28,
      getRowId: params => params.data?.id?.toString(),
      suppressPropertyNamesCheck: true,
      columnMenu: "new",
      defaultColDef: {
        sortable: true,
        resizable: true,
        editable: false,
        filter: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderMenuButton: true,
        icons: {
          filter: TABLE_FILTER_ICON
        },
        menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
        filterParams: {
          excelMode: "windows",
          values: params => {
            if (params && params.colDef && params.colDef.field) {
              this.subscriptions.push(
                this.perimetersService.perimeters$
                  .pipe(
                    switchMap<Perimeter[], Observable<number[]>>(perimeters => {
                      const { api } = params;
                      return this.quickFiltersService.getAvailableFilterValues<number[]>(params.colDef.field, {
                        buildings: PerimetersService.getBuildingsIds(perimeters),
                        ...{ filterModel: api.getFilterModel() },
                        table: "Asset"
                      });
                    })
                  )
                  .subscribe(res => {
                    if (params.colDef.allowNull) params.success([null, ...res]);
                    else params.success(res);
                  })
              );
            }
          },
          // Disable ag-grid's default filter values sorting
          comparator: (_a, _b) => {},
          refreshValuesOnOpen: true
        },
        cellStyle: params => {
          if (
            params.column?.isFilterActive() &&
            params.column?.getColId() !== "technical_state_notation" &&
            params.column?.getColId() !== "last_technical_evaluation"
          ) {
            return { color: "#ff6c6c" };
          }
          return {};
        }
      },
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: this.translate.instant("pages.common.ag-grid.columns"),
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            // Hide every other tools
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true
            }
          },
          {
            id: "filters",
            labelDefault: this.translate.instant("pages.common.ag-grid.filters"),
            labelKey: "filters",
            iconKey: "filter",
            toolPanel: "agFiltersToolPanel"
          }
        ],
        position: "right" // Can set the side bar on the left or on the right
        // Set this to open the sidebar by default on columns panel
        // defaultToolPanel: 'columns',
      },
      columnDefs: [], // Columns definition (Set it later)
      animateRows: true, // Row animations (when expanding)
      // Always show the column menus
      suppressMenuHide: true,
      groupHeaderHeight: 0, // Hide the columns groups in the header (Set it to 20 to show)
      components: {
        lastTechnicalStateRenderer: LastTechnicalStateRendererComponent,
        lastTechnicalStateFilter: LastTechnicalStateFilterComponent,
        technicalStateRenderer: TechnicalStateRendererComponent,
        technicalStateFilter: TechnicalStateFilterComponent
      },
      // Restrict aggregations some functions (sum in an ag-grid native one)
      onGridReady: params => {
        this.gridApi = params.api;
        let colsThatNeedFilterValues = [];
        this.subscriptions.push(
          combineLatest(this.route.queryParams, this.yearsService.startYear$)
            .pipe(
              map(([queryParams, startYear]) => {
                let year: number;
                if (queryParams["year"]) {
                  year = Number(queryParams["year"]);
                } else {
                  year = startYear;
                }
                return year;
              }),
              switchMap(year =>
                this.spreadsheetColumnsService.getAgGridAssetColumns(year, {
                  onDialogClose: this.onPriceSheetsDialogClose.bind(this)
                }, this.importMode)
              ),
              tap(cols => {
                // Load columns
                this.gridApi.setGridOption("columnDefs", cols);
                this.restoreState();
                // @ts-ignore
                this.gridApi.getToolPanelInstance("columns").collapseColumnGroups();
                if (this.initialFilterModel) {
                  this.gridApi.setFilterModel(this.initialFilterModel);
                }
              }),
              switchMap(() => this.isUrlParamsFilters()),
              tap(isUrlParamsFilters => {
                if (!isUrlParamsFilters && this.isInsightSpreadsheetPage) {
                  const filterModel = this.applyCacheFiltersAndSorts();
                  colsThatNeedFilterValues = Object.keys(filterModel);
                }
              }),
              switchMap(() => (this.isInputFilter() ? of([]) : this.applyURLParamsFilters())),
              tap((cols: TableFilterColumns[]) => colsThatNeedFilterValues.push(...cols)),
              switchMap(() => this.applyInputFilters()),
              tap((cols: TableFilterColumns[]) => colsThatNeedFilterValues.push(...cols)),
              switchMap(() => (this.isInsightSpreadsheetPage ? this.applyQuickFilters() : of([]))),
              tap((cols: TableFilterColumns[]) => colsThatNeedFilterValues.push(...cols)),
              map(quickFilterCols => !!quickFilterCols?.length),
              switchMap(isQuickFiltered => {
                this.isQuickFiltered = isQuickFiltered;
                if (colsThatNeedFilterValues?.length) {
                  return combineLatest([
                    this.perimetersService.perimeters$,
                    this.yearsService.startYear$,
                    this.route.queryParams
                  ]).pipe(
                    switchMap(([perimeters, year, queryParams]) =>
                      combineLatest([
                        ...colsThatNeedFilterValues.map(colId =>
                          this.quickFiltersService.getAvailableFilterValues(colId, {
                            buildings: PerimetersService.getBuildingsIds(perimeters),
                            ...{ filterModel: this.gridApi.getFilterModel() },
                            year: queryParams["year"] ? Number(queryParams["year"]) : year,
                            table: "Asset"
                          })
                        )
                      ])
                    )
                  );
                } else {
                  return of([]);
                }
              }),
              debounceTime(500)
            )
            .subscribe(() => {
            if (this.shouldApplyEOLFilter) {
              this.applyEndOfLifeFilter()
            }
            this.finishedFirstFiltering = true;
            this.urlParamsApplied = true;
            this.gridApi?.onFilterChanged();
            if (this.refreshLoading) {
              this.isRefreshing = false;
              this.refreshLoading = false;
              this.loading = false;
              this.cdRef.detectChanges();
              this.refreshSelectedAsset();
            }
          }),
        );
      },
      // Hook executed after first data set
      onFirstDataRendered: () => {
        this.loadingColumnsAndFilters = false; // End the first loading
        this.exportsService.assetExportReady.next(true);
      },
      // Hook executed after each data update
      onRowDataUpdated: () => {
        if (this.gridApi) {
          this.gridApi.moveColumns(["row_nb"], 0);
          this.gridApi.setColumnsVisible(["row_nb"], true);
          this.gridApi.refreshCells({ columns: ['row_nb'], force: true });
        }
        this.exportsService.assetExportReady.next(true);
        if (this.refreshLoading) {
          this.isRefreshing = false;
          this.refreshLoading = false;
          this.loading = false;
          this.cdRef.detectChanges();
          this.refreshSelectedAsset();
        }
      },
      onStateUpdated: () => {
        this.saveState();
      },
      onModelUpdated: () => {
        if (this.refreshLoading) {
          this.isRefreshing = false;
          this.refreshLoading = false;
          this.loading = false;
          this.gridApi?.refreshCells({ columns: ['row_nb'], force: true });
          this.cdRef.detectChanges();
          this.refreshSelectedAsset();
        }
      },
      onColumnGroupOpened: () => {
        this.saveState();
      },
      onColumnVisible: () => {
        this.gridApi.moveColumns(["row_nb"], 0);
      },
      getContextMenuItems: (_params) => {
        return this.buildContextMenu();
      },
      onFilterOpened: event => {
        // This is a hack to check if the user has opened at least one filter in order
        // to differenciate filter changes from user and the ones from the code itself
        if (event.source !== "NO_UI") {
          this.userOpenedFilter = true;
        }
      },
      onFilterChanged: () => {
        this.assetIds.emit([]);
        if (this.gridApi) {
          // Check it's not the first loading
          // Retrieve current filters model
          const filterModel = this.gridApi.getFilterModel();

          const filtersNb = Object.keys(filterModel).length;
          this.filtersNb.emit(filtersNb);

          if (!filterModel?.end_of_life) {
            localStorage.setItem("eolFilterClearedByUser", "true");
          } else {
            localStorage.removeItem("eolFilterClearedByUser");
          }

          if (
            !this.loadingColumnsAndFilters &&
            this.isInsightSpreadsheetPage &&
            (this.userOpenedFilter || this.isQuickFiltered)
          ) {
            this.subscriptions.push(
              this.isUrlParamsFilters()
                .pipe(filter(isUrlParamsFilters => !isUrlParamsFilters))
                .subscribe(() => {
                  // Save filters model in cache
                  localStorage.setItem(this.filtersLocalStorageKey, JSON.stringify(filterModel));
                })
            );
          }
          const filterKey = `asset_${this.filterContext}`;
          this.quickFiltersService.setcurrentFilterModel(filterKey as any, filterModel);
          this.quickFiltersService.setFiltersFromSpreadsheet(filterKey as any, filterModel);
        }
      },
      onCellDoubleClicked: params => {
        if (params.event?.target) {
          const timeoutId = (params.event.target as any).__priceSheetClickTimeout;
          if (timeoutId) {
            clearTimeout(timeoutId);
            (params.event.target as any).__priceSheetClickTimeout = null;
          }
        }

        if (params.data?.id && params.data.site) {
          this.assetsService
            .getAsset(params.data.id)
            .pipe(first())
            .subscribe(asset => {
              this.selectedAsset = asset as unknown as KPI.MappedAsset;
              this.showEquipmentDrawer = true;
              this.cdRef.detectChanges();
            });
        }
      },
      onSelectionChanged: () => {
        this.setSelectedRowsNb();
      },
      onCellSelectionChanged: () => {
        this.setSelectedRowsNb();
      },
      onCellFocused: () => {
        this.setSelectedRowsNb();
      },
      onRowSelected: () => {
        this.setSelectedRowsNb();
      },
      onCellValueChanged: (params) => {
        if (params?.colDef?.field === 'point_de_controle' &&
            params.newValue !== params.oldValue &&
            params.data?.id) {
          this.saveCheckpointToLocalStorage(params.data.id, params.newValue);
          this.showSuccessToast();
        }
      }
    };
    this.subscriptions.push(
      this.spreadsheetColumnsService.getAgGridTranslations().subscribe(agGridTranslations => {
        this.gridOptions.localeText = agGridTranslations;
      })
    );
  }

  protected applyCacheFiltersAndSorts() {
    const filterModelJSON = localStorage.getItem(this.filtersLocalStorageKey);
    if (filterModelJSON) {
      const filterModel: { [key: string]: any } = JSON.parse(filterModelJSON);
      if (filterModel && Object.keys(filterModel).length) {
        this.gridApi.setFilterModel(filterModel);
      } else {
        this.gridApi.setFilterModel(null);
      }
      return filterModel;
    }
    return {};
  }

  protected applyQuickFilters(): Observable<TableFilterColumns[]> {
    const filterKey = `asset_${this.filterContext}`;
    return combineLatest([
      this.quickFiltersService.getFilters(filterKey as any, true),
      this.route.queryParams,
    ]).pipe(
      map(([filters, params]) => {
        const colsThatNeedFilterValues: TableFilterColumns[] = [];
        const filterKeys = Object.keys(params).some((key) =>
          key.startsWith(this.filterKeyPrefix),
        );
        if (!filterKeys || this.urlParamsApplied) {
          Object.keys(filters).map((column: TableFilterColumns) => {
            if (!filters[column] || !filters[column].length) {
              this.gridApi.destroyFilter(column);
              const colDef = this.gridApi?.getColumnDef(column);
              if (!colDef?.lockPosition) {
                this.gridApi?.setColumnsVisible([column], false);
              }
            } else {
              this.gridApi?.getColumnFilterInstance(column).then((filterInstance) => {
                const values = Array.isArray(filters[column])
                  ? filters[column].map((v) => v !== null ? v.toString() : null)
                  : filters[column];
                if (
                  (numberColumns.includes(column) || numberVariableColumns.some((c) => column.includes(c)))
                  && values
                ) {
                  const model = this.quickFiltersService.buildNumberFilter(values);
                  if (model) {
                    filterInstance?.setModel(model);
                  }
                } else if (filterInstance) {
                  filterInstance?.setModel({
                    filterType: 'set',
                    values,
                  });
                }
                const colDef = this.gridApi.getColumnDef(column);
                const isLocked = colDef?.lockPosition;

                if (!isLocked) {
                  const columns = this.gridApi?.getColumnState();
                  let targetIndex: number = this.firstColIndexBySpreadsheetType[this.spreadsheetType];
                  let targetIndexFound: boolean = false;
                  columns?.forEach((_col, i) => {
                    if (!isLocked && !targetIndexFound) {
                      targetIndex = i;
                      targetIndexFound = true;
                    }
                  });
                  this.gridApi?.moveColumns([column], targetIndex);
                  this.gridApi?.setColumnsVisible([column], true);
                }
                colsThatNeedFilterValues.push(column);
              });
            }
          });
        }
        return colsThatNeedFilterValues;
      }),
    );
  }

  private applyEndOfLifeFilter(): void {
    const clearedByUserKey = "eolFilterClearedByUser";
    const currentYear = new Date().getFullYear();
    const eolFilter = {
      filterType: "number",
      type: "lessThanOrEqual",
      filter: currentYear + 3
    };

    const currentFilterModel = this.gridApi.getFilterModel() || {};

    if (currentFilterModel?.end_of_life) {
      if (this.isInsightSpreadsheetPage) {
        localStorage.setItem(this.filtersLocalStorageKey, JSON.stringify(currentFilterModel));
      }
      localStorage.removeItem(clearedByUserKey);
      return;
    }

    if (localStorage.getItem(clearedByUserKey) === "true") {
      return;
    }

    const cachedFiltersJSON = localStorage.getItem(this.filtersLocalStorageKey);
    const cachedFilters = cachedFiltersJSON ? JSON.parse(cachedFiltersJSON) : {};
    const filterToApply = cachedFilters?.end_of_life || eolFilter;

    const mergedFilterModel = {
      ...currentFilterModel,
      end_of_life: filterToApply
    };
    this.gridApi.setFilterModel(mergedFilterModel);
    if (this.isInsightSpreadsheetPage) {
      localStorage.setItem(this.filtersLocalStorageKey, JSON.stringify(mergedFilterModel));
    }
  }

  ngOnChanges(changes) {
    this.exportsService.assetExportReady.next(false);
    if (
      changes.level &&
      typeof changes.level.currentValue !== "undefined" &&
      changes.level.currentValue !== null &&
      changes.level.currentValue !== changes.level.previousValue &&
      this.isInsightAssetsPage
    ) {
      this.gridApi.getColumnFilterInstance("technical_state_notation").then(filterInstance => {
        // Apply the set of values as a filter to the column
        filterInstance.setModel({
          filterType: "set",
          values: [changes.level.currentValue]
        });
        this.gridApi?.onFilterChanged();
      });
    }
  }

  /**
   * Used to retrieve filters from URL and apply it on the grid's columns
   */
  private applyURLParamsFilters(): any {
    const colsThatNeedFilterValues = [];
    return this.route.queryParams.pipe(
      filter(() => !this.urlParamsApplied),
      switchMap((params) => {
        // Retrieve the filter keys from URL parameters
        const filterKeys = Object.keys(params).filter((key) =>
          key.startsWith(this.filterKeyPrefix),
        );

        const filterPromises = [];

        filterKeys.forEach((filterKey) => {
          // Get the column name from the URL param key
          let column = filterKey.substring(this.filterKeyPrefix.length);

          // Split the values from URL param
          let values = [];
          if (params[filterKey].includes('","')) {
            values = params[filterKey].split('","');
          } else {
            values = params[filterKey].split(",");
          }

          // Remove the (") from the value if any exists at the beginning or the end
          values = values.map(value => {
            if (value.indexOf('"') === 0) {
              value = value.substring(1);
              if (value.indexOf('"') === value.length - 1) {
                return value.substring(0, value.length - 1);
              }
              return value;
            } else if (value.indexOf('"') === value.length - 1) {
              return value.substring(0, value.length - 2);
            }
            return value;
          });

          const filterPromise = this.gridApi.getColumnFilterInstance(column).then((filterInstance) => {
            const applyModel = (model: any): Promise<void> =>
              new Promise<void>(resolve => {
                const result = filterInstance.setModel(model);
                result ? result.then(() => resolve()) : resolve();
              });

            // In case of an investment category filter, split the filter value
            // in order to retrieve each investment category value to filter
            if (["investment_period", "investment_priority", "investment_sub_category"].includes(column)) {
              // Apply the set of values as a filter to the column
              return applyModel({
                filter: values[0].toString(),
                filterType: "text",
                type: "contains"
              });
            } else if (numberColumns.includes(column)) {
              const model = this.quickFiltersService.buildNumberFilter(values[0]);
              if (model) {
                return applyModel(model);
              }
            } else if (column === "technical_state_notation") {
              // Apply the set of values as a filter to the column
              return applyModel({
                filterType: "set",
                values: values.map(v => v !== "null" ? v : null)
              });
            } else if (column === "last_technical_evaluation") {
              // Apply the set of values as a filter to the column
              return applyModel({
                filterType: "set",
                values: values.map(v => v !== "null" ? v : null)
              });
            } else if (filterInstance) {
              // Apply the set of values as a filter to the column
              return applyModel({
                filterType: "set",
                values: values
              });
            }
            return Promise.resolve();
          });

          filterPromises.push(filterPromise);

          if (!numberColumns.includes(column)) {
            colsThatNeedFilterValues.push(column);
          }
        });

        return from(Promise.all(filterPromises).then(() => {
          return colsThatNeedFilterValues;
        }));
      })
    );
  }

  private setInitialFilterModel(params: any): void {
    const filterKeys = Object.keys(params).filter(key => key.startsWith(this.filterKeyPrefix));
    if (filterKeys.length > 0) {
      this.initialFilterModel = {};
      filterKeys.forEach(filterKey => {
        let column = filterKey.substring(this.filterKeyPrefix.length);
        let values = [];
        if (params[filterKey].includes('","')) {
          values = params[filterKey].split('","');
        } else {
          values = params[filterKey].split(",");
        }
        values = values.map(value => {
          if (value.indexOf('"') === 0) {
            value = value.substring(1);
            if (value.indexOf('"') === value.length - 1) {
              return value.substring(0, value.length - 1);
            }
            return value;
          } else if (value.indexOf('"') === value.length - 1) {
            return value.substring(0, value.length - 2);
          }
          return value;
        });
        if (["technical_state_notation", "last_technical_evaluation"].includes(column)) {
          values = values.map(value => value !== "null" ? value : null);
        }
        this.initialFilterModel[column] = {
          filterType: "set",
          values: values
        };
      });
    }
  }

  // private applyPendingUrlParams(): void {
  //   if (!this.pendingUrlParams || !this.gridApi) {
  //     return;
  //   }

  //   const params = this.pendingUrlParams;
  //   const filterKeys = Object.keys(params).filter(key => key.startsWith(this.filterKeyPrefix));

  //   filterKeys.forEach(filterKey => {
  //     let column = filterKey.substring(this.filterKeyPrefix.length);
  //     let values = [];
  //     if (params[filterKey].includes('","')) {
  //       values = params[filterKey].split('","');
  //     } else {
  //       values = params[filterKey].split(",");
  //     }
  //     values = values.map(value => {
  //       if (value.indexOf('"') === 0) {
  //         value = value.substring(1);
  //         if (value.indexOf('"') === value.length - 1) {
  //           return value.substring(0, value.length - 1);
  //         }
  //         return value;
  //       } else if (value.indexOf('"') === value.length - 1) {
  //         return value.substring(0, value.length - 2);
  //       }
  //       return value;
  //     });

  //     this.gridApi.getColumnFilterInstance(column).then(filterInstance => {
  //       if (filterInstance) {
  //         filterInstance.setModel({
  //           filterType: "set",
  //           values: values
  //         });
  //       }
  //     });
  //   });
  //   this.pendingUrlParams = null;
  //   setTimeout(() => {
  //     this.gridApi?.onFilterChanged();
  //     this.gridApi?.refreshServerSide();
  //   }, 100);
  // }

  private updateAssetsSummary(filteredAssets: number, nextCapex: number) {
    if (this.isInsightAssetsPage || this.isInsightSpreadsheetPage || this.showTotals) {
      this.techStateAssets.techStateAssetsNb = filteredAssets;
      this.techStateAssets.loaded = true;
      this.techStateAssets.nextCapex = this.i18n.formatPrice(nextCapex).join(" ");
      this.totalAsset.emit(this.techStateAssets);
      this.filteredAssetsNb.emit(filteredAssets);
    } else {
      this.assetsWithInvests.filteredAssetsNb = filteredAssets;
      this.assetsWithInvests.assetsIdsWithInvest.push([]);
      this.assetsWithInvests.investNumber = 0;
      this.assetsWithInvests.loaded = true;
      this.assetsAndInvests.emit(this.assetsWithInvests);
      this.filteredAssetsNb.emit(filteredAssets);
    }
  }

  public updateGridAfterCellEditing(result: KPI.CellEditorResult) {
    this.assetsChangesService.addChange(result.id, result.field, result.value);
  }

  private isInputFilter(): boolean {
    return !!(this.assetsListFilter && Object.keys(this.assetsListFilter)?.length);
  }

  /**
   * Used to apply filters from Input (Example: displaying the table in a dialog)
   * In this case there Shouldn't be any filters in the URL
   */
  private applyInputFilters(): Observable<string[]> {
    const colsThatNeedFilterValues = [];
    if (!this.inputFiltersApplied && this.isInputFilter()) {
      const inputFilterKeys = Object.keys(this.assetsListFilter);
      inputFilterKeys.map(column => {
        const values = this.assetsListFilter[column].map(v => v.toString());
        this.gridApi.getColumnFilterInstance(column).then(filterInstance => {
          if (filterInstance) {
            // Apply the set of values as a filter to the column
            filterInstance.setModel({
              filterType: "set",
              values: values
            });
          }
        });
        colsThatNeedFilterValues.push(column);
      });
    }
    return of(colsThatNeedFilterValues);
  }

  public ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
    const sub = this.isUrlParamsFilters().pipe(
      filter((isUrlParamsFilters) => isUrlParamsFilters),
      first(),
    ).subscribe(() => {
      const filterKey = `asset_${this.filterContext}`;
      this.quickFiltersService.setFiltersFromSpreadsheet(filterKey as any, {});
    });
    sub.unsubscribe();
  }

  private serverSideDatasource() {
    return {
      getRows: params => {
        if (!this.finishedFirstFiltering) {
          return params.success({
            lastRow: 0,
            rowCount: 0,
            rowData: [{ label: this.noDataSentence, id: 0 }],
            rowMax: 0
          });
        }
        combineLatest([
          this.perimetersService.perimeters$.pipe(first()),
          this.investmentScenariiService.mainScenario$.pipe(first()),
          this.yearsService.startYear$.pipe(first()),
          this.route.queryParams.pipe(first()),
          this.insightFiltersService.selectedGlobalFilter$.pipe(first()),
          this.importsService.validateImport$
        ])
          .pipe(
            tap(() => {
              if (
                params.request.sortModel.length &&
                params.request.sortModel[0].colId === "technical_state_notation" &&
                !params.request.filterModel.technical_state_notation
              ) {
                params.request.filterModel.technical_state_notation = {
                  values: [null, 0, 1, 2, 3],
                  filterType: "set"
                };
              }
              if (
                params.request.sortModel.length &&
                params.request.sortModel[0].colId === "last_technical_evaluation" &&
                !params.request.filterModel.last_technical_evaluation
              ) {
                params.request.filterModel.last_technical_evaluation = {
                  values: [null, 0, 1, 2, 3],
                  filterType: "set"
                };
              }
            }),
            switchMap(([perimeters, scenario, year, queryParams, selectedGlobalFilter, validateImport]) => {
              const include_hypothesis = selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS;

              return this.spreadsheetColumnsService
                .getAssetsData({
                  ...params.request,
                  buildings: PerimetersService.getBuildingsIds(perimeters),
                  scenario: scenario.id,
                  year: queryParams["year"] ? Number(queryParams["year"]) : year,
                  include_hypothesis
                })
                .pipe(
                  map(data => ({
                    ...data,
                    rowData: data.rowData.map(row => ({
                      ...row,
                      point_de_controle: this.loadCheckpointForAsset(row.id),
                      technical_state_notation: include_hypothesis
                        ? row.technical_state_notation_including_hypothesis
                        : row.technical_state_notation
                    }))
                  })),
                  tap(data => {
                    if (validateImport) {
                      const selectedRows = this.gridApi.getSelectedRows();
                      if (selectedRows?.length > 0) {
                        // remove selected rows
                        const selectedRowsIds = selectedRows.map(row => row.id);
                        const rowData = data.rowData.filter(row => !selectedRowsIds.includes(row.id));
                        data.rowData = rowData;
                        this.clearSelection();
                      }
                    }
                  })
                );
            }),
            finalize(() => this.quickFiltersReady.emit(true))
          )
          .subscribe(data => {
            params.success(data);
            this.quickFiltersReady.emit(true);
            this.updateAssetsSummary(data["rowCount"], data["totalInvestmentCurrentPrice"]);
            const rows: IRowNode<any>[] = [];
            this.gridApi.forEachNode(rowNode => rows.push(rowNode));
            this.assetIds.emit(
              // @ts-ignore
              rows?.filter(row => !!row).map(row => row.data?.id) || []
            );
            if (this.importMode) {
              this.totalAsset.emit(data["rowCount"]);
            }
          });
        // this.subscriptions.push(
        //   of(this.finishedFirstFiltering).pipe(
        //     tap((v) => console.log("finishedFiltering", v)),
        //     switchMap((finishedFirstFiltering) =>
        //       finishedFirstFiltering
        //         ? combineLatest([
        //           this.perimetersService.perimeters$,
        //           this.investmentScenariiService.mainScenario$,
        //           this.yearsService.startYear$,
        //           this.route.queryParams.pipe(first()),
        //           this.insightFiltersService.selectedGlobalFilter$,
        //         ]).pipe(
        //           // tap((values) => console.log('getRows', values)),
        //           switchMap(([perimeters, scenario, year, queryParams, selectedGlobalFilter]) => {
        //             const include_hypothesis = selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS;
        //             if (
        //               params.request.sortModel.length &&
        //               params.request.sortModel[0].colId ===
        //               'technical_state_notation' &&
        //               !params.request.filterModel.technical_state_notation
        //             ) {
        //               params.request.filterModel.technical_state_notation = {
        //                 values: [null, 0, 1, 2, 3],
        //                 filterType: 'set',
        //               };
        //             }
        //             return this.spreadsheetColumnsService.getAssetsData({
        //               ...params.request,
        //               buildings: PerimetersService.getBuildingsIds(perimeters),
        //               scenario: scenario.id,
        //               year: queryParams['year'] ? Number(queryParams['year']) : year,
        //             }).pipe(
        //               map((data) => ({
        //                 ...data,
        //                 rowData: data.rowData.map((row) => ({
        //                   ...row,
        //                   technical_state_notation: include_hypothesis
        //                     ? row.technical_state_notation_including_hypothesis
        //                     : row.technical_state_notation,
        //                 })),
        //               })),
        //             );
        //           }),
        //         ) : of({
        //           lastRow: 0,
        //           rowCount: 0,
        //           rowData: [{ label: this.noDataSentence, id: 0 }],
        //           rowMax: 0,
        //         })
        //     )
        //   ).subscribe({
        //     next: (data) => {
        //       params.success(data);
        //       this.quickFiltersReady.emit(true);
        //       this.updateAssetsSummary(data['rowCount'], data['rowMax']);
        //       const rows: IRowNode<any>[] = [];
        //       this.gridApi.forEachNode((rowNode) => rows.push(rowNode));
        //       this.assetIds.emit(
        //         // @ts-ignore
        //         rows?.filter((row) => !!row).map((row) => row.data?.id) || [],
        //       );
        //     },
        //     error: (error) => {
        //       console.error(error);
        //       params.fail();
        //     },
        //   }),
        // );
      }
    };
  }

  /**
   * Build the cells context menu
   */
  protected buildContextMenu(): MenuItemDef[] {
    const contextMenu: MenuItemDef[] = [];
    this.gridApi.deselectAll();
    const rowNodes = this.getSelectedRows();

    const validRowNodes = rowNodes.filter(node =>
      node.data &&
      node.data.id &&
      node.data.id !== 0 &&
      node.data.id !== null &&
      node.data.id !== undefined
    );

    if (validRowNodes.length === 0) {
      return contextMenu;
    }

    const isSingleSelect = validRowNodes.length === 1;

    // Has user permission ?
    // We consider that the user doesn't have permission if at least one site in the selection
    // is read_only
    const siteIds = validRowNodes.map(node => node.data.site);
    const readOnlyPermissions = this.permissions.filter(
      perm => siteIds.includes(perm.perimeter) && perm.read_only
    );
    const readOnly = readOnlyPermissions?.length > 0;

    if (validRowNodes[0] && isSingleSelect) {
      contextMenu.push({
        name: this.translate.instant('Supprimer l\'équipement'),
        disabled: readOnly,
        tooltip: readOnly
          ? this.translate.instant("You don't have permission to delete equipment")
          : "",
        action: () => {
          this.deleteSingleEquipment(validRowNodes[0].data.id, readOnly);
        }
      });
    }

    // Multi-select delete
    if (validRowNodes.length > 1) {
      contextMenu.push({
        name: this.translate.instant('Supprimer les équipements'),
        disabled: readOnly,
        tooltip: readOnly
          ? this.translate.instant("You don't have permission to delete equipment")
          : "",
        action: () => {
          this.deleteMultipleEquipments(validRowNodes, readOnly);
        }
      });
    }

    // To PPA
    const allAssetsEligible = validRowNodes.every((node) => (node).data?.['eligible_automatic_multi_year_plan'] === true);
    const isWithinYearRange = validRowNodes.every(node => node.data?.["end_of_life"] <= this.endYear);
    contextMenu.push({
      disabled: !allAssetsEligible || readOnly,
      name: this.translate.instant("Launch automatic pluriannual plan"),
      tooltip: !allAssetsEligible
        ? this.translate.instant("Not every equipments are eligible")
        : !isWithinYearRange
        ? this.translate.instant("Incorrect period")
        : "",
      action: () => {
        this.goToPPA(validRowNodes.map(node => node.data?.['asset_id']).join(','));
      }}
    )

    return contextMenu;
  }

  private goToPPA(assetIds: string) {
    this.selectedAssetIds = assetIds.split(",").map(id => parseInt(id));
    this.showPpaDrawer = true;
    this.cdRef.detectChanges();
  }

  public closePpaDrawer(): void {
    this.showPpaDrawer = false;
    this.selectedAssetIds = [];
    this.cdRef.detectChanges();
  }

  public closeEquipmentDrawer(): void {
    this.showEquipmentDrawer = false;
    this.selectedAsset = null;
    this.cdRef.detectChanges();
  }

  public onToggleChanged(_event: {type: 'maintenance' | 'compliance', value: boolean}): void {
    this.isRefreshing = true;
    this.refreshLoading = true;
    this.cdRef.detectChanges();
    this.refreshRequested.emit();
    setTimeout(() => {
      if (this.refreshLoading || this.isRefreshing) {
        this.isRefreshing = false;
        this.refreshLoading = false;
        this.loading = false;
        this.cdRef.detectChanges();
      }
    }, 5000);
  }

  public handleRefreshRequest(): void {
    if (this.isRefreshing) {
      this.loading = true;
      this.cdRef.detectChanges();
    }
  }

  public resetRefreshLoading(): void {
    this.isRefreshing = false;
    this.refreshLoading = false;
    this.loading = false;
    this.cdRef.detectChanges();
  }

  public refreshSelectedAsset(): void {
    if (this.showEquipmentDrawer && this.selectedAsset?.id) {
      this.assetsService
        .getAsset(this.selectedAsset.id)
        .pipe(first())
        .subscribe(asset => {
          this.selectedAsset = Object.assign({}, asset as unknown as KPI.MappedAsset);
          this.cdRef.detectChanges();
        });
    }
  }

  public openAssetSheet(assetId: number): void {
    this.dialogService.open(SheetDialogComponent, {
      autoFocus: false,
      context: {
        assetId: assetId,
        title: this.translate.instant("Asset details")
      }
    });
  }

  /** Same panel as double-clicking a row (AssetFormDrawerComponent) — used by the missing-data flag column. */
  public openAssetDrawer(assetId: number): void {
    this.assetsService
      .getAsset(assetId)
      .pipe(first())
      .subscribe(asset => {
        this.selectedAsset = asset as unknown as KPI.MappedAsset;
        this.showEquipmentDrawer = true;
        this.cdRef.detectChanges();
      });
  }

  private getSelectedRows(): RowNode<any>[] {
    const selectedRanges = this.gridApi.getCellRanges();
    const selectedNodes: Set<RowNode<any>> = new Set();

    if (selectedRanges?.length) {
      selectedRanges.forEach(range => {
        const startIndex = range.startRow?.rowIndex;
        const endIndex = range.endRow?.rowIndex;

        if (startIndex != null && endIndex != null) {
          const from = Math.min(startIndex, endIndex);
          const to = Math.max(startIndex, endIndex);

          for (let i = from; i <= to; i++) {
            const node = this.gridApi.getDisplayedRowAtIndex(i);
            if (node) {
              selectedNodes.add(node as unknown as RowNode<any>);
            }
          }
        }
      });
    }

    return Array.from(selectedNodes);
  }

  private setSelectedRowsNb() {
    this.selectedRowsNb.emit(this.gridApi.getSelectedRows().length);
    const rowNodes = [];
    this.gridApi.forEachNode(node => {
      if (node?.data?.id) rowNodes.push(node);
    });
    this.loadedRowsNb.emit(rowNodes.length);
    this.cdRef.detectChanges();
  }

  public clearSelection() {
    this.gridApi.deselectAll();
    this.gridApi.clearRangeSelection();
    this.gridApi.clearFocusedCell();

    this.selectedRowsNb.emit(this.gridApi.getSelectedRows().length);
    this.cdRef.detectChanges();
  }

  public onPriceSheetsDialogClose(params: any) {
    if (params?.priceSheetsChanges) {
      const rowNode = this.gridApi!.getSelectedRows();
      rowNode[0].price_sheets_count = params.psTotal;
      setTimeout(() => this.gridApi?.redrawRows());
    }
  }

  private showSuccessToast(): void {
    this.saveToast.showSaved();
  }

  private showErrorToast(message: string): void {
    const iconConfig: NbIconConfig = {
      icon: "alert-circle-outline",
      pack: "eva"
    };
    this.toastrService.show(
      message,
      this.translate.instant("Error"),
      {
        icon: iconConfig,
        status: "danger",
        duration: 5000
      }
    );
  }

  private deleteSingleEquipment(assetId: number, readOnly: boolean): void {
    if (readOnly) return;

    this.subscriptions.push(
      this.assetsService.checkDelete([assetId]).pipe(
        switchMap((result) => {
          if (!result.can_delete) {
            this.dialogService.open(BasicDialogComponent, {
              autoFocus: false,
              context: {
                title: this.translate.instant("Information"),
                content: this.translate.instant("The selected equipment is linked to an investment already planned"),
                contentBis: this.translate.instant("Please remove the investment before deleting the equipment"),
                canCancel: true
              }
            });
            return of(null);
          }

          const content = this.translate.instant("The selected equipment(s) will be permanently deleted");
          const contentBis = result.has_hypothesis
            ? this.translate.instant("NB : the hypothesis investment linked to the equipment will be deleted too")
            : '';

          const dialogRef = this.dialogService.open(BasicDialogComponent, {
            autoFocus: false,
            context: {
              title: this.translate.instant("Confirmation"),
              content: content,
              contentBis: contentBis
            }
          });

          return dialogRef.onClose.pipe(
            first(),
            filter((confirmed) => !!confirmed),
            tap(() => {
              this.loading = true;
              this.cdRef.detectChanges();
            }),
            switchMap(() => this.assetsService.deleteAsset(assetId)),
            tap(() => {
              this.showSuccessToast();
              this.refreshLoading = true;
              this.isRefreshing = true;
              this.cdRef.detectChanges();
              this.gridApi?.refreshServerSide();
            }),
          );
        }),
        catchError((error) => {
          console.error('Error deleting equipment:', error);
          this.showErrorToast(this.translate.instant("Failed to delete equipment"));
          this.loading = false;
          this.cdRef.detectChanges();
          return of(null);
        }),
      ).subscribe()
    );
  }

  private deleteMultipleEquipments(validRowNodes: any[], readOnly: boolean): void {
    if (readOnly) return;

    const assetIds = validRowNodes.map(node => node.data.id);

    this.subscriptions.push(
      this.assetsService.checkDelete(assetIds).pipe(
        switchMap((result) => {
          if (!result.can_delete) {
            const statusName = result.status_label || '';
            this.dialogService.open(BasicDialogComponent, {
              autoFocus: false,
              context: {
                title: this.translate.instant("Information"),
                content: this.translate.instant("At least one equipment is linked to an investment whose status is \"[status]\"", { status: statusName }),
                contentBis: this.translate.instant("Please remove the investments before deleting the equipments"),
                canCancel: true
              }
            });
            return of(null);
          }

          const content = this.translate.instant("The selected equipments will be permanently deleted.");
          const contentBis = result.has_hypothesis
            ? this.translate.instant("NB : the hypothesis investments attached to the equipment will be deleted too")
            : '';

          const dialogRef = this.dialogService.open(BasicDialogComponent, {
            autoFocus: false,
            context: {
              title: this.translate.instant("Confirmation"),
              content: content,
              contentBis: contentBis
            }
          });

          return dialogRef.onClose.pipe(
            first(),
            filter((confirmed) => !!confirmed),
            tap(() => {
              this.loading = true;
              this.cdRef.detectChanges();
            }),
            switchMap(() => from(assetIds).pipe(
              concatMap(id => this.assetsService.deleteAsset(id)),
              toArray()
            )),
            tap(() => {
              this.showSuccessToast();
              this.refreshLoading = true;
              this.isRefreshing = true;
              this.cdRef.detectChanges();
              this.gridApi?.refreshServerSide();
            }),
          );
        }),
        catchError((error) => {
          console.error('Error deleting equipments:', error);
          this.showErrorToast(this.translate.instant("Failed to delete equipment"));
          this.loading = false;
          this.cdRef.detectChanges();
          return of(null);
        }),
      ).subscribe()
    );
  }

  private saveCheckpointToLocalStorage(assetId: number, value: boolean): void {
    const storageKey = 'asset_checkpoints';
    const checkpoints = this.getCheckpointsFromLocalStorage();
    checkpoints[assetId] = value;
    localStorage.setItem(storageKey, JSON.stringify(checkpoints));
  }

  private getCheckpointsFromLocalStorage(): { [assetId: number]: boolean } {
    const storageKey = 'asset_checkpoints';
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  }

  private loadCheckpointForAsset(assetId: number): boolean {
    const checkpoints = this.getCheckpointsFromLocalStorage();
    return checkpoints[assetId] || false;
  }
}
